import { useCallback, useEffect, useState } from "react";
import { Archive } from "lucide-react";
import { ORIGIN_LABEL } from "../../lib/courseLibraryApi";
import { courseArchiveApi, type ArchiveJob, type ArchivePreview, type ArchiveStatus } from "../../lib/courseArchiveApi";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";
import { toast } from "../ui/Toast";
import { mapApiError } from "../ui/StatusMessage";

const mb = (b: number) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`);
const LABEL: Record<string, string> = { notes: "Notes", quizzes: "Quizzes", content: "Content", exams: "Exams" };

/** Archive status and the confirmed course zip. Nothing is sent until the admin presses Confirm. */
export function CourseArchivePanel({ token, courseId }: { token: string; courseId: string }) {
  const [status, setStatus] = useState<ArchiveStatus | null>(null);
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState(false);
  const [preview, setPreview] = useState<ArchivePreview | null>(null);
  const [job, setJob] = useState<ArchiveJob | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const s = await courseArchiveApi.status(token, courseId);
      setStatus(s);
      setJob((j) => j ?? (s.job && (s.job.state === "building" || s.job.state === "uploading") ? s.job : null));
    } catch {
      setStatus(null);
    }
  }, [token, courseId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!open) return;
    setPreview(null);
    courseArchiveApi.preview(token, courseId, drafts).then(setPreview).catch((e) => toast.error(mapApiError(e).message));
  }, [open, drafts, token, courseId]);

  // Progress: building, uploading part x of y, done.
  useEffect(() => {
    if (!job || job.state === "done" || job.state === "failed") return;
    const id = window.setInterval(() => {
      courseArchiveApi.job(token, courseId, job.id).then((j) => {
        setJob(j);
        if (j.state === "done") toast.success("Course archived to Telegram");
        if (j.state === "failed") toast.error(j.error ?? "The archive could not be sent.");
        if (j.state === "done" || j.state === "failed") void load();
      }).catch(() => undefined);
    }, 2000);
    return () => window.clearInterval(id);
  }, [job, token, courseId, load]);

  async function confirm() {
    setBusy(true);
    try {
      setJob(await courseArchiveApi.start(token, courseId, drafts));
      setOpen(false);
    } catch (e) {
      toast.error(mapApiError(e).message, { action: { label: "Try again", onClick: () => void confirm() } });
    } finally {
      setBusy(false);
    }
  }

  async function retry(id: string) {
    try {
      await courseArchiveApi.retry(token, id);
      toast.success("Queued again");
      void load();
    } catch (e) {
      toast.error(mapApiError(e).message);
    }
  }

  if (!status) return null;
  const failed = status.recent.filter((f) => f.state === "failed");
  const running = job && (job.state === "building" || job.state === "uploading");

  return (
    <section aria-label="Telegram archive" className="flex flex-wrap items-center gap-3 rounded-tile border border-border bg-card p-3">
      <span className="flex items-center gap-2 text-sm font-semibold text-foreground"><Archive size={16} aria-hidden="true" /> Telegram archive</span>
      {!status.enabled ? (
        <Badge variant="secondary">Off</Badge>
      ) : (
        <span className="flex flex-wrap items-center gap-1.5 text-xs">
          <Badge variant="success">{status.files.sent} sent</Badge>
          {status.files.pending > 0 && <Badge variant="info">Archive pending {status.files.pending}</Badge>}
          {status.files.failed > 0 && <Badge variant="destructive">Archive failed {status.files.failed}</Badge>}
        </span>
      )}
      {running && job && (
        <span className="text-xs text-muted-foreground" role="status">
          {job.progress}{job.parts_total > 1 ? ` (${job.parts_done}/${job.parts_total})` : ""}
        </span>
      )}
      {job?.state === "done" && <span className="text-xs text-success">Last zip sent{job.file_name ? `: ${job.file_name}` : ""}</span>}
      <Button size="sm" variant="outline" className="ml-auto" disabled={!status.enabled || Boolean(running)} onClick={() => setOpen(true)}>
        Archive course to Telegram
      </Button>
      {failed.length > 0 && (
        <ul className="w-full space-y-1 text-xs">
          {failed.slice(0, 5).map((f) => (
            <li key={f.id} className="flex items-center justify-between gap-2 rounded-md bg-soft px-2 py-1">
              <span className="min-w-0 truncate text-muted-foreground">{f.kind} v{f.version} · {f.file_name}{f.error ? ` · ${f.error}` : ""}</span>
              <Button size="sm" variant="ghost" onClick={() => void retry(f.id)}>Retry</Button>
            </li>
          ))}
        </ul>
      )}

      <Modal isOpen={open} onClose={() => setOpen(false)} title="Archive this course to Telegram?" size="sm">
        {!preview ? (
          <p className="text-sm text-muted-foreground">Checking what would be sent…</p>
        ) : (
          <div className="space-y-3 text-sm">
            {!preview.has_approved && !drafts && <p className="rounded-tile bg-warning/10 p-2 text-warning">This course has no approved content yet. Tick the drafts option to archive drafts.</p>}
            <ul className="space-y-1">
              {Object.entries(preview.files).map(([k, n]) => (
                <li key={k} className="flex justify-between"><span className="text-muted-foreground">{LABEL[k] ?? k}</span><span className="font-semibold text-foreground">{n} files</span></li>
              ))}
              {Object.entries(preview.origins ?? {}).map(([m, kinds]) => (
                <li key={m} className="flex justify-between"><span className="text-muted-foreground">Origin: {ORIGIN_LABEL[m] ?? m}</span><span className="font-semibold text-foreground">{Object.values(kinds).reduce((a, b) => a + b, 0)} items</span></li>
              ))}
              <li className="flex justify-between border-t border-border pt-1"><span className="text-muted-foreground">Estimated zip size</span><span className="font-semibold text-foreground">{mb(preview.estimated_bytes)}</span></li>
              <li className="flex justify-between"><span className="text-muted-foreground">Goes to</span><span className="font-semibold text-foreground">{preview.destination}</span></li>
            </ul>
            {preview.split && <p className="rounded-tile bg-soft p-2">It is over the Telegram limit, so it will be sent as {preview.parts} numbered parts.</p>}
            {preview.previous && (
              <p className="rounded-tile bg-soft p-2 text-muted-foreground">An older zip of this course already exists{preview.previous.at ? ` (${new Date(preview.previous.at).toLocaleDateString()})` : ""}. This sends a new one; the old one stays.</p>
            )}
            {preview.error && <p role="alert" className="text-destructive">{preview.error}</p>}
            <label className="flex items-center gap-2"><input type="checkbox" checked={drafts} onChange={(e) => setDrafts(e.target.checked)} className="h-4 w-4" /> Include drafts</label>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button loading={busy} loadingText="Starting…" disabled={preview.total_files === 0 || Boolean(preview.error) || (!preview.has_approved && !drafts)} onClick={() => void confirm()}>
                Confirm and send
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </section>
  );
}
